import { useState, useMemo } from 'react';
import {
  Home,
  Package,
  Settings,
  ChefHat,
  ClipboardCheck,
  Truck,
  Warehouse,
  BarChart3,
} from 'lucide-react';
import {
  Sidebar as BillSidebar,
  AppSwitcher,
  BrandSwitcher,
  BILL_APPS,
  filterAppsByProducts,
  type SidebarNavItem,
} from '@bill/ui';
import { Product } from '@bill/core-types';
import { useAuth } from '@/context/AuthContext';
import { useBrandContext } from '@/hooks';
import bstockLogo from '@/assets/bstock-logo-white.svg';
import { NotificationBell, NotificationDrawer } from '@/features/notifications';
import { UserRole, hasPermission as checkPermission, hasMinimumRole, type Permission } from '@bstock/shared';
import { WhatsNewBadge } from '@/features/changelog';
import { getRouteAccess, isAccessAllowed } from '@/config/route-access';

// Nav items augmented with permission/role metadata. The metadata is stripped
// before passing to the generic @bill/ui Sidebar.
interface BStockNavSubItem {
  to: string;
  label: string;
  permission?: Permission;
  minRole?: UserRole;
}

type BStockNavItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  permission?: Permission;
  minRole?: UserRole;
} & (
  | { to: string; children?: never }
  | { to?: never; children: BStockNavSubItem[] }
);


const ALL_NAV_ITEMS: BStockNavItem[] = [
  { key: 'dashboard', to: '/', label: 'Dashboard', icon: <Home className="h-5 w-5" /> },
  {
    key: 'products',
    label: 'Produits',
    icon: <Package className="h-5 w-5" />,
    children: [
      {
        to: '/products/pos',
        label: 'Produits finis (POS)',
        permission: 'pos-products.read',
        minRole: UserRole.MANAGER,
      },
      { to: '/products/raw', label: 'Produits bruts', permission: 'raw-products.read' },
    ],
  },
  {
    key: 'recipes',
    to: '/recettes',
    label: 'Recettes',
    icon: <ChefHat className="h-5 w-5" />,
    permission: 'recipes.read',
  },
  {
    key: 'controls',
    label: 'Controles',
    icon: <ClipboardCheck className="h-5 w-5" />,
    children: [
      { to: '/controles/ventes-pos', label: 'Ventes POS', permission: 'pos-sales.read' },
      { to: '/controles/audit-logs', label: 'Journal d\'activite', permission: 'audit.read', minRole: UserRole.MANAGER },
    ],
  },
  {
    key: 'purchases',
    label: 'Achats',
    icon: <Truck className="h-5 w-5" />,
    children: [
      { to: '/achats/commandes', label: 'Commandes', permission: 'purchase-orders.read' },
      { to: '/achats/receptions', label: 'Receptions', permission: 'goods-receipts.read' },
      { to: '/achats/retours-fournisseur', label: 'Retours fournisseur', permission: 'supplier-returns.read', minRole: UserRole.MANAGER },
    ],
  },
  {
    key: 'stock',
    label: 'Stock',
    icon: <Warehouse className="h-5 w-5" />,
    permission: 'stock.read',
    children: [
      { to: '/stock/actuel', label: 'Stock actuel', permission: 'stock.read' },
      { to: '/stock/mouvements', label: 'Mouvements', permission: 'stock.read' },
      { to: '/stock/inventaires', label: 'Inventaires', permission: 'inventories.read' },
      { to: '/stock/pertes', label: 'Pertes', permission: 'losses.read' },
      { to: '/stock/transferts', label: 'Transferts', permission: 'transfers.read' },
    ],
  },
  {
    key: 'reports',
    label: 'Rapports',
    icon: <BarChart3 className="h-5 w-5" />,
    permission: 'reports.read',
    children: [
      { to: '/rapports/stock', label: 'Rapport de Stock', permission: 'reports.read' },
      { to: '/rapports/marge', label: 'Rapport de Marge', permission: 'reports.read_financials' },
      { to: '/rapports/achats', label: 'Sommaire Achats', permission: 'reports.read' },
      { to: '/rapports/product-mix', label: 'Product Mix', permission: 'reports.read' },
    ],
  },
  {
    key: 'settings',
    to: '/parametres',
    label: 'Parametres',
    icon: <Settings className="h-5 w-5" />,
  },
];

export function Sidebar() {
  const { user, moduleAccess, logout, switchBrand, isSwitchingBrand } = useAuth();
  const { isGroup, brandType } = useBrandContext();

  const availableApps = useMemo(
    () => filterAppsByProducts(BILL_APPS, moduleAccess?.availableProducts),
    [moduleAccess?.availableProducts],
  );

  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);

  const role = user?.currentBrand?.bstockRole ?? user?.currentBrand?.role;
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const hasPermissionForItem = (permission?: Permission, minRole?: UserRole): boolean => {
    if (isSuperAdmin) return true;
    if (!permission && !minRole) return true;
    if (!role) return false;
    if (permission && !checkPermission(role, permission)) return false;
    if (minRole && !hasMinimumRole(role, minRole)) return false;
    return true;
  };

  // Filter + order nav items based on brand context and permissions, then
  // strip the BStock-specific metadata to match @bill/ui's SidebarNavItem shape.
  const navItems: SidebarNavItem[] = useMemo(() => {
    if (!role && !isSuperAdmin) return [];
    if (!brandType) return [];

    const filtered = ALL_NAV_ITEMS
      .filter((item) => {
        if (item.to) {
          const access = getRouteAccess(item.to);
          if (!isAccessAllowed(access, brandType)) return false;
        }
        if (!hasPermissionForItem(item.permission, item.minRole)) return false;
        return true;
      })
      .map((item) => {
        if (item.children) {
          const visibleChildren = item.children.filter((child) => {
            if (!hasPermissionForItem(child.permission, child.minRole)) return false;
            if (child.to) {
              const access = getRouteAccess(child.to);
              if (!isAccessAllowed(access, brandType)) return false;
            }
            return true;
          });
          if (visibleChildren.length === 0) return null;
          return { ...item, children: visibleChildren };
        }
        return item;
      })
      .filter((item): item is BStockNavItem => item !== null);

    // For non-group brands (sites and centrals), reorder:
    // Dashboard, Stock, Achats, Rapports, Controles
    const ordered = !isGroup
      ? [...filtered].sort((a, b) => {
          const siteOrder = ['dashboard', 'stock', 'purchases', 'reports', 'controls'];
          const indexA = siteOrder.indexOf(a.key);
          const indexB = siteOrder.indexOf(b.key);
          const orderA = indexA === -1 ? 999 : indexA;
          const orderB = indexB === -1 ? 999 : indexB;
          return orderA - orderB;
        })
      : filtered;

    // Strip metadata to match @bill/ui's SidebarNavItem shape
    return ordered.map<SidebarNavItem>((item) => {
      if (item.children) {
        return {
          key: item.key,
          label: item.label,
          icon: item.icon,
          children: item.children.map(({ to, label }) => ({ to, label })),
        };
      }
      return {
        key: item.key,
        label: item.label,
        icon: item.icon,
        to: item.to,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, brandType, role, isSuperAdmin]);

  return (
    <BillSidebar
      logo={
        <AppSwitcher
          trigger={<img src={bstockLogo} alt="BStock" className="h-8" />}
          apps={availableApps}
          currentAppId="bstock"
        />
      }
      navItems={navItems}
      headerActions={
        <NotificationBell
          onClick={() => setIsNotificationDrawerOpen(true)}
          className="text-sidebar-icon hover:text-white hover:bg-sidebar-hover"
        />
      }
      brandSwitcher={
        user && user.companies.length > 0 ? (
          <BrandSwitcher
            companies={user.companies}
            currentBrand={user.currentBrand}
            onSwitch={switchBrand}
            isLoading={isSwitchingBrand}
            // US-MAC.6 — n'autoriser le switch que vers les etablissements
            // qui ont reellement le module BStock accessible (souscrit + role).
            filterBrand={(brand) => brand.availableProducts?.includes(Product.BSTOCK) ?? true}
          />
        ) : undefined
      }
      user={
        user
          ? {
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              // Affiche le role EFFECTIF sur BStock : bstockRole si pose,
              // sinon fallback sur role legacy (bypass admin/owner).
              role: user.currentBrand?.bstockRole ?? user.currentBrand?.role,
            }
          : undefined
      }
      onLogout={logout}
      footerExtras={<WhatsNewBadge />}
    >
      <NotificationDrawer
        open={isNotificationDrawerOpen}
        onClose={() => setIsNotificationDrawerOpen(false)}
      />
    </BillSidebar>
  );
}
